import { useCallback, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'nv-theme'

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : null
  } catch {
    // Private browsing / blocked storage — fall back to the system preference.
    return null
  }
}

/**
 * Theme state, persisted across visits.
 *
 * The initial paint is handled by the inline script in index.html, which sets
 * `data-theme` before React boots. This hook adopts whatever that script
 * decided so the two never disagree.
 */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    const attr = document.documentElement.dataset.theme
    if (attr === 'light' || attr === 'dark') return attr
    return storedTheme() ?? systemTheme()
  })

  /** True while the visitor is riding the OS preference rather than an explicit choice. */
  const [followsSystem, setFollowsSystem] = useState(() => storedTheme() === null)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  // Track the OS while no explicit choice has been made.
  useEffect(() => {
    if (!followsSystem) return
    const query = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (event: MediaQueryListEvent) => setTheme(event.matches ? 'light' : 'dark')
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [followsSystem])

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark'
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // Non-fatal: the theme still applies for this session.
      }
      return next
    })
    setFollowsSystem(false)
  }, [])

  return { theme, toggle }
}
